import { useState } from 'react';
import { authedFetch, type SessionState } from '../lib/session.js';

interface ExtractedMeeting {
  title: string;
  contactNameGuess: string | null;
  contactEmailGuess: string | null;
  accountNameGuess: string | null;
  startIso: string;
  endIso: string;
  summary: string;
  language: string;
}
interface PreviewResult {
  extracted: ExtractedMeeting;
  matchedAccountId: string | null;
  matchedContactId: string | null;
  duplicateEventId: string | null;
  plannedWriteId: string | null;
  zohoBody: Record<string, unknown>;
  reasons: string[];
}

interface Props { session: SessionState }

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid var(--border)', borderRadius: 6,
  fontFamily: 'inherit', background: 'var(--surface)', color: 'var(--fg)',
};

export function LogMeetingButton({ session }: Props) {
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [phase, setPhase] = useState<'compose' | 'previewing' | 'preview' | 'confirming' | 'done' | 'error'>('compose');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  // Manual quick-entry mode (no transcript / no Granola).
  const [mode, setMode] = useState<'transcript' | 'manual'>('transcript');
  const [mTitle, setMTitle] = useState('');
  const [mContact, setMContact] = useState('');
  const [mWhen, setMWhen] = useState('');
  const [mNotes, setMNotes] = useState('');

  function reset() {
    setOpen(false);
    setTranscript('');
    setPhase('compose');
    setPreview(null);
    setError(null);
    setEventId(null);
    setMode('transcript');
    setMTitle(''); setMContact(''); setMWhen(''); setMNotes('');
  }

  async function runPreview() {
    setPhase('previewing');
    setError(null);
    try {
      const r = await authedFetch(session, '/api/dashboard/log-meeting/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail ?? j.error ?? `preview_${r.status}`);
      setPreview(j);
      setPhase('preview');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  async function runPreviewManual() {
    setPhase('previewing');
    setError(null);
    try {
      const startIso = mWhen; // naive datetime-local; server reads it as Europe/Madrid
      const r = await authedFetch(session, '/api/dashboard/log-meeting/preview-manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: mTitle, startIso, contactEmail: mContact || undefined, notes: mNotes || undefined }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail ?? j.error ?? `preview_${r.status}`);
      setPreview(j);
      setPhase('preview');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  async function runConfirm() {
    if (!preview?.plannedWriteId) return;
    setPhase('confirming');
    setError(null);
    try {
      const r = await authedFetch(session, '/api/dashboard/log-meeting/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plannedWriteId: preview.plannedWriteId }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.detail ?? j.error ?? `confirm_${r.status}`);
      setEventId(j.eventId ?? null);
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  return (
    <>
      <button className="action-btn" onClick={() => setOpen(true)} title="Pega la transcripción de Granola/Fellow/Meet y se sube a Zoho como evento">
        ＋ Registrar reunión
      </button>
      {open && (
        <div className="overlay" onClick={reset}>
          <div className="overlay-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
              <div>
                <div className="topbar-brand">Registrar reunión · Zoho</div>
                <h2 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600 }}>
                  {phase === 'done' ? '✓ Reunión registrada' : 'Registrar reunión en Zoho'}
                </h2>
              </div>
              <button className="tab" onClick={reset}>✕ Cerrar</button>
            </div>

            {phase === 'compose' && (
              <>
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  <button className={`tab ${mode === 'transcript' ? 'tab-active' : ''}`} onClick={() => setMode('transcript')}
                    style={{ fontWeight: mode === 'transcript' ? 700 : 400 }}>Pegar transcripción</button>
                  <button className={`tab ${mode === 'manual' ? 'tab-active' : ''}`} onClick={() => setMode('manual')}
                    style={{ fontWeight: mode === 'manual' ? 700 : 400 }}>Manual (rápido)</button>
                </div>

                {mode === 'transcript' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '0 0 10px' }}>
                      Pega el resumen de Granola / Fellow / Meet. La IA extrae empresa, contacto, fecha y resumen. Tú revisas antes de subir.
                    </p>
                    <textarea
                      value={transcript}
                      onChange={(e) => setTranscript(e.target.value)}
                      placeholder="Pega aquí la transcripción o el resumen de Granola..."
                      style={{
                        width: '100%', minHeight: 240, padding: 12, fontSize: 13,
                        border: '1px solid var(--border)', borderRadius: 6,
                        fontFamily: 'inherit', resize: 'vertical',
                        background: 'var(--surface)', color: 'var(--fg)',
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 12 }}>
                      <span style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>{transcript.length} caracteres</span>
                      <button className="action-btn" disabled={transcript.trim().length < 30} onClick={runPreview}
                        style={{ opacity: transcript.trim().length < 30 ? 0.5 : 1 }}>Revisar vista previa →</button>
                    </div>
                  </>
                )}

                {mode === 'manual' && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '0 0 10px' }}>
                      Rellena lo justo. Se busca el contacto en Zoho por su email y la reunión queda a tu nombre.
                    </p>
                    <div style={{ display: 'grid', gap: 10 }}>
                      <Field label="Título *"><input value={mTitle} onChange={(e) => setMTitle(e.target.value)} placeholder="Reunión con..." style={inputStyle} /></Field>
                      <Field label="Email del contacto"><input value={mContact} onChange={(e) => setMContact(e.target.value)} placeholder="contacto@empresa.com" style={inputStyle} /></Field>
                      <Field label="Cuándo *"><input type="datetime-local" value={mWhen} onChange={(e) => setMWhen(e.target.value)} style={inputStyle} /></Field>
                      <Field label="Notas"><textarea value={mNotes} onChange={(e) => setMNotes(e.target.value)} placeholder="De qué se habló, próximos pasos..." style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} /></Field>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                      <button className="action-btn" disabled={mTitle.trim().length < 3 || !mWhen} onClick={runPreviewManual}
                        style={{ opacity: (mTitle.trim().length < 3 || !mWhen) ? 0.5 : 1 }}>Revisar vista previa →</button>
                    </div>
                  </>
                )}
              </>
            )}

            {phase === 'previewing' && (
              <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--fg-muted)' }}>Analizando con IA…</div>
            )}

            {phase === 'preview' && preview && (
              <PreviewView preview={preview} onCancel={() => setPhase('compose')} onConfirm={runConfirm} />
            )}

            {phase === 'confirming' && (
              <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--fg-muted)' }}>Escribiendo a Zoho…</div>
            )}

            {phase === 'done' && (
              <div style={{ padding: '20px 0' }}>
                <div style={{ background: 'var(--ok-soft)', color: 'var(--ok)', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                  ✓ Evento creado en Zoho{eventId ? ` (id: ${eventId})` : ''}. Aparecerá en el panel en la siguiente actualización.
                </div>
                <button className="action-btn" onClick={reset}>Listo</button>
              </div>
            )}

            {phase === 'error' && (
              <div style={{ padding: '20px 0' }}>
                <div style={{ background: 'var(--danger-soft)', color: 'var(--danger)', padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                  {error}
                </div>
                <button className="action-btn secondary" onClick={() => setPhase('compose')}>Volver</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function PreviewView({ preview, onCancel, onConfirm }: { preview: PreviewResult; onCancel: () => void; onConfirm: () => void }) {
  const e = preview.extracted;
  const isDup = !!preview.duplicateEventId;
  return (
    <>
      {isDup && (
        <div style={{ background: 'var(--warn-soft)', color: 'var(--warn)', padding: 12, borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
          ⚠ Ya existe un evento en Zoho con título parecido a esta hora (id <code>{preview.duplicateEventId}</code>). No se creará duplicado.
        </div>
      )}
      {preview.reasons.some((r) => r.startsWith('zoho_user_not_found')) && (
        <div style={{ background: '#402d00', color: '#ffce7a', padding: '8px 10px', borderRadius: 4, fontSize: 12, marginBottom: 12 }}>
          ⚠ Tu usuario no existe en Zoho con este email: la Reunión quedará a nombre del usuario de integración, no tuyo. Pide que te den de alta en Zoho con el mismo email para ser el dueño.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 14px', fontSize: 13, marginBottom: 16 }}>
        <Label>Título</Label><Value>{e.title}</Value>
        <Label>Cuándo</Label><Value>{e.startIso.slice(0, 16).replace('T', ' ')} → {e.endIso.slice(11, 16)}</Value>
        <Label>Contacto</Label>
        <Value>
          {e.contactNameGuess ?? '-'}
          {e.contactEmailGuess && <span style={{ color: 'var(--fg-subtle)' }}> · {e.contactEmailGuess}</span>}
          {preview.matchedContactId ? <Pill color="ok">✓ existe en Zoho</Pill> : e.contactEmailGuess ? <Pill color="warn">no encontrado</Pill> : null}
        </Value>
        <Label>Empresa</Label>
        <Value>
          {e.accountNameGuess ?? '-'}
          {preview.matchedAccountId ? <Pill color="ok">✓ existe en Zoho</Pill> : e.accountNameGuess ? <Pill color="warn">no encontrada</Pill> : null}
        </Value>
        <Label>Resumen</Label>
        <Value style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto', background: 'var(--surface-hi)', padding: 8, borderRadius: 4 }}>
          {e.summary}
        </Value>
      </div>

      {preview.reasons.length > 0 && (
        <details style={{ marginBottom: 14, fontSize: 11, color: 'var(--fg-subtle)' }}>
          <summary style={{ cursor: 'pointer' }}>Notas técnicas ({preview.reasons.length})</summary>
          <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
            {preview.reasons.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </details>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="action-btn secondary" onClick={onCancel}>← Volver</button>
        <button
          className="action-btn"
          onClick={onConfirm}
          disabled={isDup}
          style={{ opacity: isDup ? 0.5 : 1 }}
          title={isDup ? 'Ya existe, no se creará duplicado' : 'Sube el evento a Zoho ahora'}
        >
          {isDup ? 'Duplicado, no se sube' : 'Confirmar y subir a Zoho →'}
        </button>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 11, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600 }}>{children}</div>;
}
function Value({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ color: 'var(--fg)', ...style }}>{children}</div>;
}
function Pill({ children, color }: { children: React.ReactNode; color: 'ok' | 'warn' }) {
  const bg = color === 'ok' ? 'var(--ok-soft)' : 'var(--warn-soft)';
  const fg = color === 'ok' ? 'var(--ok)' : 'var(--warn)';
  return <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 3, background: bg, color: fg, fontWeight: 600 }}>{children}</span>;
}
